<template>
  <Transition name="overlay-fade">
    <!-- IMPORTANTE: backdrop z-40, nunca z-50 — fica atrás do modal de detalhes (z-50). -->
    <!-- Em z-50 empataria e venceria pela ordem no DOM, cobrindo o modal. -->
    <div
      v-if="modelValue && backdrop"
      class="fixed z-40 inset-0 bg-black/20"
      @click="closeSidebar"
    ></div>
  </Transition>

  <Transition name="sidebar-slide">
    <div
      v-if="modelValue"
      data-testid="sidebar-panel"
      class="fixed z-50 top-0 right-0 bottom-0 w-screen md:w-[var(--sidebar-width)] h-full shadow-xl flex flex-col"
      :style="{ backgroundColor: props.backgroundColor, '--sidebar-width': props.width }"
    >
      <slot name="header" :close="closeSidebar">
        <div class="flex flex-col items-start gap-4 mb-6 p-6 pb-0">
          <button
            data-testid="sidebar-close-button"
            @click="closeSidebar"
            class="text-textPrimary hover:text-textPrimary/50 transition mt-1"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-6 h-6"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path :d="mdiArrowRight" />
            </svg>
          </button>

          <div class="flex-col items-center text-secondary50 justify-between w-full">
            <div v-if="title" class="flex items-center gap-3 justify-between">
              <span v-if="headerChildren" v-html="headerChildren"></span>
              <div class="flex items-center gap-3">
                <svg
                  v-if="icon"
                  :class="props.sizeIcon && 'w-8 h-8'"
                  class="w-6 h-6 shrink-0"
                  :fill="iconFillColor"
                  viewBox="0 0 24 24"
                >
                  <path :d="icon" />
                </svg>
                <h2 class="text-2xl font-bold text-left">{{ title }}</h2>
              </div>
              <div
                v-if="iconRight"
                @click="handleIconRightClick"
                @mouseover="isHovering = true"
                @mouseleave="isHovering = false"
                class="w-10 h-10 flex items-center justify-center text-end rounded-full border-2 border-[#2F7F91] transition-all cursor-pointer"
                :class="iconRightClasses"
              >
                <svg
                  :class="props.sizeIcon ? 'w-5 h-5' : 'w-4 h-4'"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path :d="props.iconRight" />
                </svg>
              </div>
            </div>

            <p v-if="description" class="text-md text-left mt-4">
              {{ description }}
            </p>
          </div>
          <hr
            class="w-full border-t border-[#2F7F91]/25"
            :class="props.iconRight && '!border-t-[3px] !border-[#2F7F91]'"
          />
        </div>
      </slot>

      <div class="flex-1 overflow-y-auto px-6 scrollbar-gutter-stable">
        <div v-if="$slots.contextHeader" class="md:hidden pb-3">
          <slot name="contextHeader"></slot>
        </div>
        <slot></slot>
      </div>
      <div v-if="$slots.footer" class="p-6 pt-0">
        <slot name="footer"></slot>
      </div>
    </div>
  </Transition>
</template>
<script setup lang="ts">
import { mdiArrowRight } from '@mdi/js'
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    title?: string
    description?: string
    icon?: string
    iconRight?: string
    sizeIcon?: boolean
    /** Injetado com `v-html`, sem sanitizar: nunca passe texto vindo do usuário. */
    headerChildren?: string
    isIconRightActive?: boolean
    backdrop?: boolean
    backgroundColor?: string
    // Largura do painel em telas md+ (mobile é sempre full-screen). Default 450px.
    width?: string
  }>(),
  {
    backdrop: true,
    backgroundColor: '#E2E2E9',
    width: '450px'
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'openFilter'): void
}>()

const isHovering = ref(false)

function closeSidebar() {
  emit('update:modelValue', false)
}

function handleIconRightClick() {
  emit('openFilter')
}

const iconRightClasses = computed(() => ({
  'bg-[#2F7F91] text-white': props.isIconRightActive || isHovering.value,
  'bg-white text-[#2F7F91]': !props.isIconRightActive && !isHovering.value
}))

const iconFillColor = computed(() => {
  return '#000'
})
</script>

<style scoped>
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 300ms ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
  transition: transform 300ms ease;
}

.sidebar-slide-enter-from,
.sidebar-slide-leave-to {
  transform: translateX(100%);
}
</style>
